import { useShoppingCart } from "@/src/context/shopping-cart/shopping-cart-context"
import { useMessage } from "@/src/context/message/message-context"
import { Overlay } from "@/src/components/helpers/overlays/overlay"
import { useCurrentProduct } from "@/src/context/product/current-product-context"
import ItemInfos from "@/src/interfaces/catalog/item-infos"
import ShoppingCart from "@/src/interfaces/system/shopping-cart"
import "@/src/extensions/string"
import { useGoogleAnalytics } from "@/src/context/google-analytics/google-analytics-context"
import { useProductValidations } from "../product/useValidations"
import { AnswerFromCustomer, ProductTypeId } from "../product/useWizard"

type itemType = "observation" | "product" | "productBySizeWithWizard" | "productBySize" | "complement" | "wizardProduct";

export const useAddItem = () => {
    const { addProduct } = useShoppingCart()
    const { validateWizard } = useProductValidations()
    const { observation, price, answersFromCustomer, sizeProduct, currentProduct } = useCurrentProduct()
    const { showMessage } = useMessage()
    const { addToCartGA } = useGoogleAnalytics()

    const getType = (): itemType => {
        if (answersFromCustomer.length > 0)
            return "productBySizeWithWizard"
        return "productBySize"
    }

    const addToCart = (productDetails: ItemInfos.DetailedResponse, quantity: number) => {
        if (!productDetails) return showMessage("Não foi possível adicionar o produto.")

        const isRedeemItem = !!productDetails.loyaltyCampaignId
        const redeemObs = isRedeemItem ? ' (ganhou)' : ''

        let orderedAnswers: AnswerFromCustomer[] = []

        if (answersFromCustomer) {
            orderedAnswers = answersFromCustomer.sort((a, b) => {
                const indexA = currentProduct?.itemGroups.findIndex((pergunta) => pergunta.id === a.id) ?? 0
                const indexB = currentProduct?.itemGroups.findIndex((pergunta) => pergunta.id === b.id) ?? 0
                return indexA - indexB
            })
        }

        if (!isRedeemItem && validateWizard().hasError)
            return

        const _totalPrice = (price * quantity).roundAwayFromZero(2)
        let cartItem: ShoppingCart.CartItem | undefined

        const isCombo = productDetails.itemTypeId === ProductTypeId.Combo
        const hasWizard = orderedAnswers?.length > 0

        let comboPrice = 0

        if (sizeProduct) {
            cartItem = {
                id: sizeProduct.itemId!,
                externalId: sizeProduct.detailsExternalId!,
                isGroup: sizeProduct.isGroup,
                hasWizard: hasWizard,
                observation: observation,
                price: sizeProduct.isGroup ? price : productDetails.salePrice,
                quantity: (sizeProduct.isGroup || hasWizard) ? 1 : quantity,
                name: `${sizeProduct.name!} ${redeemObs}`,
                type: getType(),
                personalizedName: `${sizeProduct.name} ${redeemObs}`,
                subItems: [],
                totalPrice: sizeProduct.isGroup ? price : (hasWizard ? _totalPrice / quantity : _totalPrice)
            }

            if (sizeProduct.isGroup) {
                const subItems: ShoppingCart.CartItem[] | undefined = sizeProduct.sizesSelected?.map((item) => {
                    return {
                        id: item.id,
                        externalId: item.externalId,
                        isGroup: item.isGroup,
                        price: item.salePrice,
                        quantity: item.quantity,
                        name: item.name,
                        type: 'productBySize',
                        personalizedName: item.name,
                        promoId: item.promoId,
                        promoPriceType: item.promoPriceType,
                        subItems: item.complementsSelecteds ? item.complementsSelecteds.map((complement) => {
                            const flavorsQty = (sizeProduct.sizesSelected && sizeProduct.sizesSelected.length) ?? 1
                            const roundedQty = (1 / flavorsQty).customRound(4)
                            return {
                                id: complement.id,
                                externalId: complement.productExternalId,
                                isGroup: true,
                                price: complement.salePrice,
                                quantity: roundedQty,
                                name: complement.name,
                                type: 'complement',
                                personalizedName: complement.name,
                                totalPrice: complement.salePrice
                            } as ShoppingCart.CartItem
                        }) : [],
                        totalPrice: sizeProduct.isGroup ? price : _totalPrice
                    } as ShoppingCart.CartItem
                })

                cartItem.subItems = subItems
            }
            else {
                cartItem.name = `${productDetails.name} - ${sizeProduct.name} ${redeemObs}`
                cartItem.personalizedName = undefined
            }

            if (!isRedeemItem && (answersFromCustomer && answersFromCustomer.length > 0)) {
                const wizardOrdered = orderedAnswers

                wizardOrdered.map((item) => {
                    item.itemOptionsGroup!.map((option) => {
                        let priceOption: number = option.promoPrice ?? option.price

                        if (item.type === 'complement')
                            priceOption = option.promoPrice ?? (option.price && option.price > 0 ? option.price : option.itemChild?.salePrice)

                        if (item.type === 'product' && priceOption == 0)
                            priceOption = option.promoPrice ?? option.itemChild?.salePrice ?? 0

                        const currentItem = {
                            id: option.itemId,
                            price: priceOption,
                            quantity: option.quantity,
                            name: option.answerForMerchant ? option.answerForMerchant : option.itemChild?.name,
                            type: item.type,
                            optionId: option.id,
                            groupId: option.itemGroupId
                        } as ShoppingCart.CartItem

                        cartItem?.subItems?.push(currentItem)
                    })
                })
            }
        }
        else if (!isRedeemItem && (answersFromCustomer && answersFromCustomer.length > 0)) {
            const wizardOrdered = orderedAnswers

            cartItem = {
                id: productDetails.id,
                price: productDetails.salePrice,
                quantity: (isCombo || hasWizard) ? 1 : quantity,
                externalId: productDetails.productDetailsExternalId,
                name: `${productDetails.name} ${redeemObs}`,
                totalPrice: hasWizard ? (_totalPrice / quantity) : _totalPrice,
                observation: observation,
                type: 'wizardProduct',
                isGroup: isCombo,
                hasWizard: hasWizard,
                subItems: [] as ShoppingCart.CartItem[]
            }

            let indexLastProduct: number | undefined = undefined

            wizardOrdered.map((item, index) => {
                const isProductItem = item.type === "product"
                const isObservationItem = item.itemOptionsGroup?.some(e => e.answerForCustomer && e.answerForMerchant)
                const isComplement = item.itemOptionsGroup?.some(e => e.itemChild?.itemTypeId === ProductTypeId.Complement)

                item.itemOptionsGroup!.map((option) => {
                    let priceOption: number = option.promoPrice ?? option.price

                    if (item.type === 'complement')
                        priceOption = option.promoPrice ?? (option.price && option.price > 0 ? option.price : option.itemChild?.salePrice)

                    if (item.type === 'product' && priceOption == 0)
                        priceOption = option.promoPrice ?? option.itemChild?.salePrice ?? 0

                    if (isCombo) {
                        priceOption = (priceOption / option.quantity).roundAwayFromZero(2)
                        comboPrice += (priceOption * option.quantity).roundAwayFromZero(2)
                    }

                    const currentItem = {
                        id: option.itemId,
                        optionId: option.id,
                        price: priceOption,
                        quantity: option.quantity,
                        name: (option.answerForMerchant ?? '').isNullOrEmpty() ? option.itemChild?.name : option.answerForMerchant,
                        observation: option.answerForMerchant,
                        type: isObservationItem ? 'observation' : isComplement ? 'complement' : 'product',
                        subItems: [] as ShoppingCart.CartItem[],
                        groupId: option.itemGroupId
                    } as ShoppingCart.CartItem

                    if (indexLastProduct !== undefined && !isProductItem) {
                        const lastProductCartItem = cartItem!.subItems!.at(indexLastProduct)

                        if (indexLastProduct >= 0 && index > indexLastProduct) {

                            let lastItem = cartItem!.subItems!.at(index)
                            if (currentItem.type != 'product' && !lastItem) {
                                lastItem = cartItem!.subItems!.at(indexLastProduct)

                                if (!lastItem) {
                                    indexLastProduct = cartItem!.subItems!.length - 1;
                                    lastItem = cartItem!.subItems!.at(indexLastProduct)
                                }
                            }

                            if (lastItem) {
                                if (!lastItem.subItems)
                                    lastItem.subItems = [] as ShoppingCart.CartItem[]

                                lastItem!.subItems.push(currentItem)
                            }

                        }
                        else {
                            lastProductCartItem!.subItems = [...[] as ShoppingCart.CartItem[], currentItem]
                        }

                    }
                    else {
                        cartItem?.subItems?.push(currentItem)
                    }

                    if (isProductItem)
                        indexLastProduct = index
                })
            })
        }
        else {
            cartItem = {
                id: productDetails.id,
                price: productDetails.salePrice,
                quantity: quantity,
                externalId: productDetails.productDetailsExternalId,
                name: `${productDetails.name} ${redeemObs}`,
                totalPrice: _totalPrice,
                observation: observation,
                type: 'product',
                isGroup: false,
                hasWizard: hasWizard,
                subItems: [] as ShoppingCart.CartItem[]
            }
        }

        cartItem = {
            ...cartItem,
            promoId: productDetails.promoId,
            promoPriceType: productDetails.promoPriceType,
            hasPhoto: productDetails.hasPhoto,
            smallImageUrl: productDetails.images[0] ? productDetails.images[0]?.smallImageUrl : undefined,
        }

        if (comboPrice > 0) {
            cartItem = {
                ...cartItem,
                totalPrice: comboPrice
            }
        }

        if (isRedeemItem) {
            cartItem = {
                ...cartItem,
                totalPrice: 0,
                loyaltyCampaignId: productDetails.loyaltyCampaignId,
                redeemValue: productDetails.unitRewardPoints,
            }
        }

        if ((sizeProduct && sizeProduct.isGroup) || isCombo || hasWizard) {
            for (let i = 0; i < quantity; i++) {
                addProduct(cartItem)
                addToCartGA({
                    currency: 'BRL',
                    value: cartItem.price,
                    items: [
                        {
                            item_id: cartItem.id,
                            item_name: cartItem.name
                        }
                    ]
                })
            }
        }
        else {
            addProduct(cartItem)
            addToCartGA({
                currency: 'BRL',
                value: cartItem.price,
                items: [
                    {
                        item_id: cartItem.id,
                        item_name: cartItem.name
                    }
                ]
            })
        }

        Overlay.dismiss()
    }

    return { addToCart }
}